import { Company } from 'module/api/company/types';
import {
  fieldAccessMetaToBase3,
  hasWriteAccessForField,
} from 'module/helpers/field-access';
import { fullName } from 'module/helpers/full-name';
import { FieldType } from 'module/types/filter-field/field/field';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import {
  Grid,
  withEditingMask,
  withExternalFilter,
  withSingleClickEdit,
} from 'module/ui/components/atoms/grid';
import { getValueFormatter } from 'module/ui/components/atoms/grid/formatters/get-value-formatters';
import { getValueGetter } from 'module/ui/components/atoms/grid/getters/get-value-getter';
import { getKeyCreator } from 'module/ui/components/atoms/grid/key-creators';
import {
  getCellEditorPopupPosition,
  suppressEnterAndTabKey,
  suppressEnterKey,
} from 'module/ui/components/atoms/grid/utils';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { getEditor } from 'module/ui/components/molecules/field-editors/editor';
import { getGroupedFilterFunction } from 'module/ui/components/molecules/field-filter/utils';
import { ColDef, GetRowIdParams } from 'ag-grid-community';
import { useExternalTeamMemberFieldsAsFilterableField } from 'helpers/fields/external-team-members-fields/fields';
import { Dictionary, keyBy } from 'lodash';
import { useAccountPeopleV2Query } from 'queries/account-people';
import { isEmpty, pipe } from 'ramda';
import { useCallback, useMemo } from 'react';
import { getComparator } from 'components/organisms/accounts/lists/gridconfig/compartors';
import { getRenderer } from './cell-renderer';
import { GroupRenderer } from './cell-renderer/group-renderer';
import { sortFieldsInColumnOrder } from './column-order';
import { transformPeopleListRow } from './transformer';
import {
  HoverableFields,
  NonEditableFields,
  NoneSortableFields,
  PeopleDefaultColumnFields,
  useStaticColumnFields,
} from './use-static-column-fields';
import { AccountPeopleEmptyState } from '../../empty-state/no-people';
import { AccountPeopleNoSearchResult } from '../../empty-state/no-search-result';
import { PeopleListPageParams } from '../../utils/use-page-params';

const ListGrid = pipe(
  pipe(() => Grid, withExternalFilter, withSingleClickEdit, withEditingMask),
)();
const getGridOptions = (
  fieldsLookupByFieldName: Dictionary<FilterableField>,
) => ({
  suppressContextMenu: true,
  unSortIcon: true,
  suppressCellFocus: true,
  groupDisplayType: 'groupRows' as const,
  debounceVerticalScrollbar: true,
  maintainColumnOrder: true,
  rowBuffer: 8,
  defaultColDef: {
    resizable: true,
    sortable: true,
    editable: false,
    lockPinned: true,
    suppressMovable: true,
    suppressHeaderMenuButton: true,
    cellEditorPopup: true,
    cellEditorPopupPosition: 'under' as const,
  },
  groupDefaultExpanded: -1,
  groupRowRendererParams: {
    innerRenderer: GroupRenderer,
    fieldsLookupByFieldName,
  },
  getRowId: (d: GetRowIdParams) => d.data.user.userId?.toString() ?? '',
  onRowClicked: ({ node }) => {
    node.setExpanded(!node.expanded);
  },
});

interface Props {
  pageParams: PeopleListPageParams;
  company: Company;
  clearSearch: VoidFunction;
}
const getWidth = (field: FilterableField) => {
  if (field.fieldName === PeopleDefaultColumnFields.Name) {
    return 360;
  }
  if (
    field.fieldName === PeopleDefaultColumnFields.RecentOpportunities ||
    field.fieldName === PeopleDefaultColumnFields.RecentRisks
  ) {
    return 144;
  }

  return undefined;
};

export const AccountPeopleListGrid = ({
  pageParams,
  company,
  clearSearch,
}: Props) => {
  const { fields: userFields, loading: isUserFieldsLoading } =
    useExternalTeamMemberFieldsAsFilterableField();
  const defaultFields = useStaticColumnFields();
  const columnFields = useMemo(() => {
    return sortFieldsInColumnOrder([...defaultFields, ...userFields]);
  }, [defaultFields, userFields]);
  const { data: peopleList, isLoading: isPeopleListLoading } =
    useAccountPeopleV2Query(company.companyId);
  const fieldAccessMeta = useMemo(() => {
    return peopleList && fieldAccessMetaToBase3(peopleList.fieldsAccessMeta);
  }, [peopleList]);
  const columns: ColDef[] = useMemo(() => {
    return columnFields.map((field) => ({
      field: field.fieldName,
      headerName: field.fieldLabel,
      cellRenderer: getRenderer(field),
      valueGetter: getValueGetter(field),
      valueFormatter: getValueFormatter(field),
      keyCreator: getKeyCreator(field),
      comparator: getComparator(field),
      sortable: !NoneSortableFields.includes(field.fieldName),
      lockPosition: field.fieldName === PeopleDefaultColumnFields.Name,
      pinned: field.fieldName === PeopleDefaultColumnFields.Name,
      minWidth: getWidth(field),
      cellStyle: (params) => {
        if (params.colDef.field === PeopleDefaultColumnFields.Name) {
          return { borderRight: '1px solid var(--table-border-color)' };
        }

        return undefined;
      },
      suppressKeyboardEvent:
        field.type === FieldType.MultiLineText
          ? suppressEnterAndTabKey
          : suppressEnterKey,
      rowGroup: field.fieldName === pageParams.groupBy,
      enableRowGroup: field.fieldName === pageParams.groupBy,
      editable:
        !NonEditableFields.includes(field.fieldName) &&
        hasWriteAccessForField(field, fieldAccessMeta),
      cellEditor: getEditor(field),
      cellClass:
        (!NonEditableFields.includes(field.fieldName) &&
          hasWriteAccessForField(field, fieldAccessMeta)) ||
        HoverableFields.includes(field.fieldName)
          ? 'grid-cell-editable'
          : 'grid-cell-not-editable',
      cellEditorPopupPosition: getCellEditorPopupPosition(field),
      flex: 1,
    }));
  }, [columnFields, fieldAccessMeta, pageParams.groupBy]);

  const transformedPeopleList = useMemo(() => {
    return (
      peopleList?.pages.map((row) => transformPeopleListRow(row, userFields)) ??
      []
    );
  }, [peopleList, userFields]);
  const filterFunction = useMemo(
    () => getGroupedFilterFunction(pageParams.filter),
    [pageParams.filter],
  );
  const searchFilter = useCallback(
    (data) => {
      if (!data || isEmpty(pageParams.searchQuery)) {
        return true;
      }

      const name = fullName(data.user);

      return (
        name
          .toLowerCase()
          .includes(pageParams.searchQuery?.toLowerCase() ?? '') ?? true
      );
    },
    [pageParams.searchQuery],
  );
  const fieldsLookupByFieldName = useMemo(
    () => keyBy(columnFields, 'fieldName'),
    [columnFields],
  );
  const onModelUpdated = ({ api }) => {
    if (api.getDisplayedRowCount() === 0) {
      api.showNoRowsOverlay();
    } else {
      api.hideOverlay();
    }
  };

  return isUserFieldsLoading || isPeopleListLoading ? (
    <PageLoading />
  ) : transformedPeopleList.length > 0 ? (
    <ListGrid
      {...getGridOptions(fieldsLookupByFieldName)}
      columnDefs={columns}
      rowData={transformedPeopleList}
      filter={filterFunction ?? undefined}
      search={searchFilter}
      noRowsOverlayComponent={AccountPeopleNoSearchResult}
      noRowsOverlayComponentParams={{
        onPrimaryAction: clearSearch,
      }}
      onModelUpdated={onModelUpdated}
    />
  ) : (
    <AccountPeopleEmptyState />
  );
};
